import type { Account } from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useEventsAndSubEntriesOfType } from "src/hooks/userActivity"
import type { Reading } from "src/types"
import {
  glucoseEventToReading,
  glucoseSubEntryToReading,
} from "src/utils"


const beforeSort = [{ started_at: "desc" as const }]
const afterSort = [{ started_at: "asc" as const }]

// subEntries return type from useEventsAndSubEntriesOfType
type GlucoseSubEntry = ReturnType<typeof useEventsAndSubEntriesOfType<ActivityType.Glucose>>["subEntries"][0]

/*
 * this function queries for the first glucose event and first glucose subentry
 * before and after the given time and compares them all to return the reading
 * nearest to the given time
 */
export function useNearestGlucoseReading(
  accountId: Account["id"],
  time: string,
): Reading | undefined {
  const beforeSelector = useMemo(
    () => ({
      started_at: { $lte: time },
    }),
    [time],
  )

  const afterSelector = useMemo(
    () => ({
      started_at: { $gte: time },
    }),
    [time],
  )

  const {
    // events[0] is the first glucose event before time or undefined
    events: eventsBefore,
    // subEntries[0] is the first glucose subentry before time or undefined
    subEntries: subEntriesBefore,
  } = useEventsAndSubEntriesOfType(
    accountId,
    ActivityType.Glucose,
    beforeSelector,
    1,
    beforeSort,
  )

  const {
    // events[0] is the first glucose event after time or undefined
    events: eventsAfter,
    // subEntries[0] is the first glucose subentry after time or undefined
    subEntries: subEntriesAfter,
  } = useEventsAndSubEntriesOfType(
    accountId,
    ActivityType.Glucose,
    afterSelector,
    1,
    afterSort,
  )

  // find the nearest item from all of the results
  // if this gets slow, consider waiting until request fetching is over
  return useMemo(
    () => {
      if (!time) {
        return undefined
      }

      const mapToGlucoseSubEntryToReading = (subEntry: GlucoseSubEntry) => glucoseSubEntryToReading(
        subEntry.started_at,
        subEntry,
      )

      const allResults = (eventsBefore.map(glucoseEventToReading))
        .concat(eventsAfter.map(glucoseEventToReading))
        .concat(subEntriesBefore.map(mapToGlucoseSubEntryToReading))
        .concat(subEntriesAfter.map(mapToGlucoseSubEntryToReading))

      return allResults.reduce(
        (
          accumulator: Reading | undefined,
          result: Reading,
        ) => {
          if (!accumulator) {
            return result
          }

          const accumulatorDiff = Math.abs(dayjs(time).diff(accumulator.startedAt))
          const resultDiff = Math.abs(dayjs(time).diff(result.startedAt))

          if (accumulatorDiff < resultDiff) {
            return accumulator
          }

          return result
        },
        undefined,
      )
    },
    [
      time,
      eventsBefore,
      subEntriesBefore,
      eventsAfter,
      subEntriesAfter,
    ],
  )
}
